/*
/  Google Tag Manager (GTM) consent handler for Shopify
/  Checks consent for preferences, analytics, marketing, and sale of data, and calls GTM if they are met.
/
/  To use: 
/   1. Ensure site uses shopify cookie bar
/   2. Customise the required permissions on the body tag to match client requirements.
/
*/

import Extension from "../base/extension";

class GtmConsentHandler extends Extension {

  gtmId;
  requiredPermissions = new Map();

  constructor(name) {
    super(name);
  }

  init() {
    // Get Gtm ID from meta tag in theme.liquid, allowing client to set in theme settings
    this.gtmId = document.body.dataset.gtmId;
    
    if (!this.gtmId) {
      return;
    }
    
    // Get GTM permission conditions from data attributes on the <body> tag
    this.requiredPermissions = new Map([
      ['preferencesProcessing', this.getBooleanDataAttribute('gtmPreferencesProcessing')],
      ['analyticsProcessing', this.getBooleanDataAttribute('gtmAnalyticsProcessing')],
      ['marketing', this.getBooleanDataAttribute('gtmMarketing')],
      ['saleOfData', this.getBooleanDataAttribute('gtmSaleOfData')]
    ]);
    
    // Event Listener for customer consent change. 
    // We don't pass event data to canEnableGtm as it's structured differently to the output of window.Shopify.loadFeatures
    document.addEventListener("visitorConsentCollected", e => {
      if (this.canEnableGtm()) {
        this.callGtm();
      }
    });

    // Load shopify's consent tracking API (https://shopify.dev/docs/api/customer-privacy) 
    // if successful, check customer privacy settings.
    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      error => {
        if (error) {
          console.warn(this.logPrefix, 'Error loading tracking API: ', error);
        }
        if (this.canEnableGtm()) {
          this.callGtm();
        }
      }
    );
  }

  /**
   * Helper function to get check if data attribute existsm then if is set to string "true"
   * 
   * @param {string} attributeName - The name of the data attribute.
   * 
   * @returns {boolean} - based on data attribute string value. If the data attribute does not exist, we return the strict case of `true`.
  */
  getBooleanDataAttribute(attributeName) {
    if (attributeName in document.body.dataset) {
      return document.body.dataset[attributeName] === "true";
    }
    console.warn(this.logPrefix, 'GTM data attribute not found:', attributeName);
    return true;
  }

  /**
   * Get privacy settings from Shopify customer privacy API
   * 
   */
  getPrivacySettings() {
    const privacy = window.Shopify.customerPrivacy;
    return {
      preferencesProcessing: privacy.preferencesProcessingAllowed() || false,
      analyticsProcessing: privacy.analyticsProcessingAllowed() || false,
      marketing: privacy.marketingAllowed() || false,
      saleOfData: privacy.saleOfDataAllowed() || false
    };
  }

  /**
   * Check privacy settings and return a boolean for enabling GTM.
   * 
   * @param {Map} [conditions=this.requiredPermissions] - The required privacy conditions.
   * 
   * @returns {boolean} - `true` if all required conditions are met, otherwise `false`.
   */
  canEnableGtm(conditions = this.requiredPermissions) {
    const privacySettings = this.getPrivacySettings();

    let allConditionsMet = true;

    conditions.forEach((isRequiredCondition, key) => {
      const hasGrantedPermission = privacySettings[key];
      if (isRequiredCondition && !hasGrantedPermission) {
        allConditionsMet = false;
      }
    });
  
    return allConditionsMet;
  }

  // Call Google Tag Manager
  callGtm() {
    (function(w, d, s, l, i){
      w[l] = w[l] || [];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', this.gtmId);
  }
}

export default GtmConsentHandler;
